import axios from 'axios';
import { FB_URL } from '../store/actions/types';
import { checkOpenAIStatus } from './lead_validation';

/**
 * Validates a test against a specific lead's content and category
 * @param {Object} test - The test data being submitted (all questions from all sections)
 * @param {Object} lead - The lead that the test is being created for
 * @returns {Promise<Object>} - Promise resolving to { valid: boolean, message: string }
 */
export const validateTestAgainstLead = async (test, lead) => {
  try {
    // Test mode configuration for production
    const TEST_MODE = false;
    
    if (TEST_MODE) {
      return {
        valid: true,
        message: 'TEST MODE: Your test content looks great! (API validation bypassed for testing)',
        categoryMatch: true,
        contentRelevance: true,
        apiStatus: 'test_mode',
        openAIStatus: { working: true, testMode: true }
      };
    }

    // First, check if OpenAI is working
    const openAIStatus = await checkOpenAIStatus();
    
    if (!openAIStatus.working) {
      return {
        valid: true,
        message: 'OpenAI API is not available. Your test will be submitted without validation.',
        apiStatus: 'openai_unavailable',
        openAIStatus: openAIStatus
      };
    }


    
    // Prepare test content for validation
    const testContent = prepareTestContent(test);
    
    // Validate test against lead using AI
    const validationResult = await validateWithAI(testContent, lead);
    
    // Add OpenAI status to the result
    return {
      ...validationResult,
      openAIStatus: openAIStatus
    };
  } catch (error) {
    // In case of error, default to allowing the test to proceed
    return { 
      valid: true, 
      message: 'Error during validation, proceeding with submission.',
      apiStatus: 'error',
      errorDetails: error.message,
      openAIStatus: { working: false, error: error.message }
    };
  }
};

/**
 * Prepare test content from all sections and questions
 * @param {Object} test - The test object with sections
 * @returns {string} - Formatted test content for AI validation
 */
const prepareTestContent = (test) => {
  if (!test.sections || !Array.isArray(test.sections)) {
    return '';
  }

  let testContent = '';
  
  test.sections.forEach((section, sectionIndex) => {
    if (section.questions && section.questions.length > 0) {
      testContent += `Section ${sectionIndex + 1}: ${section.name}\n`;
      
      section.questions.forEach((question, questionIndex) => {
        testContent += `Q${questionIndex + 1}: ${question.question}\n`;
        
        // Add answer options
        const answers = [question.a1, question.a2, question.a3, question.a4]
          .filter(answer => answer && answer.trim() !== '');
        answers.forEach((answer, answerIndex) => {
          testContent += `  ${String.fromCharCode(65 + answerIndex)}. ${answer}\n`;
        });
        
        // Add correct answer
        if (question.a5) {
          testContent += `  Correct Answer: ${question.a5}\n`;
        }
        testContent += '\n';
      });
      testContent += '\n';
    }
  });

  return testContent;
};

/**
 * Validate test content against lead using OpenAI
 * @param {string} testContent - Formatted test content
 * @param {Object} lead - The lead object with title and category
 * @returns {Promise<Object>} - Promise resolving to { valid: boolean, message: string }
 */
const validateWithAI = async (testContent, lead) => {
  try {
    
    // DEBUG: Log what we receive
    
    
    
    
    
    const isTranscriptAvailable = lead.transcript && lead.transcript.trim() !== '';
    
    // The content for validation is the transcript if it exists, otherwise it's the title.
    const primaryContent = isTranscriptAvailable ? lead.transcript : lead.title;
    const promptContext = isTranscriptAvailable 
      ? `The primary content for validation is the following TRANSCRIPT. The title is for context only.`
      : `The primary content for validation is the following CONTENT (which is taken from the lead's title).`;

    // DEBUG: Log what we're using for validation
    
    
    

    // Prepare the prompt for OpenAI test validation
    const prompt = `
You are an AI validator. Your only job is to determine if the "Test Content" is relevant to the "Primary Content for Validation" provided below.

${promptContext}

Title: "${lead.title}"
Primary Content for Validation: "${primaryContent}"

Test Content to Validate:
${testContent}

Is the "Test Content" relevant to the "Primary Content for Validation"?

Validation Criteria:
- You must validate the test *only* against the "Primary Content for Validation".
- Do not make assumptions. If the test is about a topic not explicitly mentioned, it is irrelevant.

Please respond with:
1. A "valid" field (boolean).
2. A "message" field (string explanation).
3. A "content_relevance" field (boolean).

Respond in JSON format.
`;

    // DEBUG: Log the full prompt being sent to AI
    

    // Set timeout for the request (using AbortController like lead validation)
    const timeoutDuration = 30000; // 30 seconds
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort();
    }, timeoutDuration);

    try {
      // Use the existing ChatGPT integration with abort signal
      const response = await axios.post(`${FB_URL}/m103-chatgpt`, {
        question: prompt,
        email: 'test_validation@thinklead.app'
      }, { 
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json'
        }
      });

      // Clear the timeout if successful
      clearTimeout(timeoutId);

      // Check both possible response formats
      const aiResponse = response.data.answer || response.data.data;
    
      if (aiResponse) {
        
        try {
          // Try to parse as JSON if it's in that format
          const jsonResponse = JSON.parse(aiResponse);
          
          return {
            valid: jsonResponse.valid === true,
            message: jsonResponse.message || 'Test has been reviewed against the lead content.',
            categoryMatch: jsonResponse.category_match || false,
            contentRelevance: jsonResponse.content_relevance || false,
            apiStatus: 'success'
          };
        } catch (e) {
          // If not JSON, perform text-based analysis
          const isValid = aiResponse.toLowerCase().includes('valid: true') || 
                          aiResponse.toLowerCase().includes('"valid": true') ||
                          (!aiResponse.toLowerCase().includes('valid: false') && 
                           !aiResponse.toLowerCase().includes('"valid": false') &&
                           !aiResponse.toLowerCase().includes('not aligned') &&
                           !aiResponse.toLowerCase().includes('inappropriate') &&
                           !aiResponse.toLowerCase().includes('irrelevant'));
          
          // Check for category match
          const categoryMatch = aiResponse.toLowerCase().includes('category') && 
                              (aiResponse.toLowerCase().includes('align') || 
                               aiResponse.toLowerCase().includes('match'));
          
          // Check for content relevance
          const contentRelevance = aiResponse.toLowerCase().includes('relevant') &&
                                 !aiResponse.toLowerCase().includes('irrelevant');
          

          
          return {
            valid: isValid,
            message: aiResponse.slice(0, 500), // Limit message length
            categoryMatch,
            contentRelevance,
            apiStatus: 'success_text_analysis'
          };
        }
      }
      
      // Handle case where response.data exists but doesn't have expected fields
      if (response.data && response.data.message === "Success") {
        return {
          valid: true, 
          message: 'Test content aligns with the lead.',
          categoryMatch: true,
          contentRelevance: true,
          apiStatus: 'success_non_standard'
        };
      }
      

      // Default to valid if we can't parse the response
      return { 
        valid: true, 
        message: 'Unable to analyze test alignment, proceeding with submission.',
        categoryMatch: false,
        contentRelevance: false,
        apiStatus: 'no_answer'
      };

    } catch (requestError) {
      // Clear timeout on error
      clearTimeout(timeoutId);
      
      // Check if it's a timeout/abort error
      if (requestError.name === 'AbortError' || requestError.code === 'ECONNABORTED') {
        return { 
          valid: true, 
          message: 'Request timed out, proceeding with submission.',
          categoryMatch: false,
          contentRelevance: false,
          apiStatus: 'timeout',
          errorDetails: 'Request timed out after 30 seconds'
        };
      }
      
      // Other request errors
      return { 
        valid: true, 
        message: 'Error during test validation, proceeding with submission.',
        categoryMatch: false,
        contentRelevance: false,
        apiStatus: 'api_error',
        errorDetails: requestError.message
      };
    }

  } catch (error) {
    return { 
      valid: true, 
      message: 'Error during test validation, proceeding with submission.',
      categoryMatch: false,
      contentRelevance: false,
      apiStatus: 'general_error',
      errorDetails: error.message
    };
  }
}; 